import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api } from '@/api/client'
import type { StoreSummary } from '@/shared/types'
import { useAuth } from '@/app/AuthProvider'

const RECENT_KEY = 'storePickerRecent'
const MAX_RECENT = 3

interface StoreContextValue {
  stores: StoreSummary[]
  storeId: string | null
  setStoreId: (id: string | null) => void
  loading: boolean
  recentIds: string[]
  refreshStores: () => Promise<void>
  selectedStore: StoreSummary | null
}

const StoreContext = createContext<StoreContextValue | null>(null)

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return []
  }
}

function writeRecent(ids: string[]) {
  localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)))
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [stores, setStores] = useState<StoreSummary[]>([])
  const [storeId, setStoreIdState] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [recentIds, setRecentIds] = useState<string[]>(readRecent)

  const refreshStores = useCallback(async () => {
    if (!user) {
      setStores([])
      return
    }
    setLoading(true)
    try {
      const data = await api<StoreSummary[] | { stores?: StoreSummary[] }>('/api/stores')
      const list = Array.isArray(data) ? data : data.stores || []
      setStores(list)
      setStoreIdState((prev) => {
        if (prev && list.some((s) => String(s.id) === prev)) return prev
        const firstRecent = readRecent().find((id) => list.some((s) => String(s.id) === id))
        return firstRecent || (list[0] ? String(list[0].id) : null)
      })
    } catch {
      setStores([])
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    void refreshStores()
  }, [refreshStores])

  const setStoreId = useCallback((id: string | null) => {
    setStoreIdState(id)
    if (!id) return
    setRecentIds((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, MAX_RECENT)
      writeRecent(next)
      return next
    })
  }, [])

  const selectedStore = useMemo(
    () => stores.find((s) => String(s.id) === storeId) || null,
    [stores, storeId],
  )

  const value = useMemo(
    () => ({
      stores,
      storeId,
      setStoreId,
      loading,
      recentIds,
      refreshStores,
      selectedStore,
    }),
    [stores, storeId, setStoreId, loading, recentIds, refreshStores, selectedStore],
  )

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export function useStore() {
  const ctx = useContext(StoreContext)
  if (!ctx) throw new Error('useStore must be used within StoreProvider')
  return ctx
}
